import { useDispatch } from "react-redux";
import { Button } from 'components/common';
import { Controller, useForm } from "react-hook-form";
import TinymceEditor from "components/common/TinymceEditor";
import React, { useCallback, useEffect, useState } from "react";
import Select, { components as RSComponents } from "react-select";
import { getProjectBuildings } from "redux/crm/project/quotationSlice";
import { objectSerializer } from "helpers/utils";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { FaTimes, FaExclamationCircle, FaBuilding, FaChevronDown } from "react-icons/fa";
import { SELECT_STYLES } from "helpers/formStyles";
import PropTypes from 'prop-types';

// The menu used to render in flow and was painted over by the TinyMCE toolbar of
// the Reason field right below it — the editor header owns a stacking context the
// in-flow menu cannot beat. Portal it to <body> instead. tailwind.css documents
// the layers: taiwincss modals 100000, TinyMCE floating UI (.tox-tinymce-aux)
// 100001 — so the menu goes above both, matching RecipientSelect.
const BUILDING_SELECT_STYLES = {
  ...SELECT_STYLES,
  menuPortal:  (base) => ({ ...base, zIndex: 100002 }),
  valueContainer: (base) => ({ ...base, paddingLeft: 4 }),
  multiValue:  (base) => ({ ...base, backgroundColor: '#eef2ff', borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: '#4f46e5', fontWeight: 600, fontSize: 12 }),
  multiValueRemove: (base) => ({ ...base, color: '#818cf8', ':hover': { backgroundColor: '#e0e7ff', color: '#4338ca' } }),
};

// Leading building glyph inside the control, so the field reads as a building
// picker at a glance instead of a bare "Select...".
const ControlWithIcon = ({ children, ...props }) => (
  <RSComponents.Control {...props}>
    <span className="tw-pl-2.5 tw-flex tw-items-center tw-text-slate-400">
      <FaBuilding size={13} />
    </span>
    {children}
  </RSComponents.Control>
);
ControlWithIcon.propTypes = { children: PropTypes.node };

const ChevronIndicator = (props) => (
  <RSComponents.DropdownIndicator {...props}>
    <FaChevronDown size={11} />
  </RSComponents.DropdownIndicator>
);

const BuildingOption = ({ children, ...props }) => (
  <RSComponents.Option {...props}>
    <span className="tw-flex tw-items-center tw-gap-2">
      <FaBuilding size={11} className="tw-text-slate-300 tw-shrink-0" />
      {children}
    </span>
  </RSComponents.Option>
);
BuildingOption.propTypes = { children: PropTypes.node };

const BuildingChip = ({ children, ...props }) => (
  <RSComponents.MultiValueLabel {...props}>
    <span className="tw-inline-flex tw-items-center tw-gap-1">
      <FaBuilding size={9} />
      {children}
    </span>
  </RSComponents.MultiValueLabel>
);
BuildingChip.propTypes = { children: PropTypes.node };

const AddChangeBuildingForm = ({showModal, setShowModal, project, building, setWaiverBuildings}) => {
  const {control, handleSubmit, formState: {errors}, setValue} = useForm();
  const [buildings, setBuildings] = useState([]);
  const dispatch = useDispatch();
  const projectId = project.id;

  const fetchBuildings = useCallback(async () => {
    const response = await dispatch(getProjectBuildings({ projectId }))
    setBuildings(objectSerializer(response.payload.rows))
  }, [dispatch, projectId])

  useEffect(() => {
    fetchBuildings()
  }, [project, fetchBuildings]);

  useEffect(() => {
    if (building) {
      Object.entries(building).forEach(([key, value]) => {
        setValue(key, value)
      })
    }
  }, [project, building, setValue])

  const buildingOptions = (
    buildings && buildings.map((building) => ({value: building.id, label: building.name }) )
  )

  const closeModal = () => {
    setShowModal(false)
  }

  const handleChangeBuilding = (selectedOptions) => {
    const selectedOptionsValues = selectedOptions.map((option) => option.value);
    setValue('deal_proj_ids', selectedOptionsValues, { shouldValidate: true })
  }

  const onSubmit = async (data) => {
    // Recompute the building_name based on updated deal_proj_ids
    const building_name = data.deal_proj_ids.map((id) => {
      const building = buildings.find((b) => b.id === id);
      return building ? building.name : '';
    });

    if (building) {
      setWaiverBuildings((prevBuildings) => {
        const newWaiverBuildings = [...prevBuildings];
        const updateIndex = newWaiverBuildings.findIndex((item) => item.id === building.id);

        // Update the building data, including the updated name
        newWaiverBuildings[updateIndex] = {
          ...data,
          name: building_name.sort().join(", "),
        };

        return newWaiverBuildings;
      });

      toast.success(`${building_name.sort().join(", ")} was successfully updated.`);
      closeModal();
    } else {
      setWaiverBuildings((prevBuildings) =>
        prevBuildings.concat({ ...data, id: uuidv4(), name: building_name.sort().join(", ") })
      );

      toast.success(`${building_name.sort().join(", ")} was successfully added.`);
    }
  };

  if (!showModal) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black/40 tw-backdrop-blur-[2px]">
      <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-3xl tw-flex tw-flex-col tw-max-h-[90vh]">
        <form onSubmit={handleSubmit(onSubmit)} className="tw-flex tw-flex-col tw-min-h-0">

          {/* Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-pt-4 tw-pb-3">
            <h3 className="tw-text-base tw-font-bold tw-text-slate-900 tw-mb-0">
              {building ? "Edit" : "Add"} building for waiver!
            </h3>
            <button
              type="button"
              onClick={closeModal}
              className="tw-w-7 tw-h-7 tw-rounded-xl tw-bg-slate-100 hover:tw-bg-slate-200 tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-text-slate-500 tw-shrink-0 tw-transition-colors"
            >
              <FaTimes size={11} />
            </button>
          </div>

          {/* Body */}
          <div className="tw-px-5 tw-pb-2 tw-overflow-y-auto">
            {/* Warning alert */}
            <div className="tw-flex tw-items-center tw-gap-3 tw-rounded tw-bg-amber-50 tw-text-amber-700 tw-px-4 tw-py-3 tw-mb-4">
              <FaExclamationCircle className="tw-shrink-0" size={22} />
              <span className="tw-text-sm tw-font-medium">
                Specify buildings and reason for waiver.
              </span>
            </div>

            {/* Buildings */}
            <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-start tw-gap-2 tw-mb-3">
              <label htmlFor="deal_proj_ids" className="tw-text-sm sm:tw-w-28 sm:tw-text-right sm:tw-pt-2 tw-shrink-0">
                Buildings
              </label>
              <div className="tw-flex-1 tw-min-w-0">
                <Controller
                  control={control}
                  name="deal_proj_ids"
                  rules={{required: "Building must be required."}}
                  render={({field}) => (
                    <Select
                      {...field}
                      inputId="deal_proj_ids"
                      closeMenuOnSelect={false}
                      options={buildingOptions}
                      // filter(Boolean): on the edit path the saved ids arrive
                      // before fetchBuildings resolves, and an unmatched id would
                      // otherwise put `undefined` into the value array.
                      value={field.value
                        ?.map((id) => buildingOptions.find((option) => option.value === id))
                        .filter(Boolean)}
                      placeholder='Select buildings...'
                      classNamePrefix="react-select"
                      isMulti
                      styles={BUILDING_SELECT_STYLES}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        Control:          ControlWithIcon,
                        DropdownIndicator: ChevronIndicator,
                        Option:           BuildingOption,
                        MultiValueLabel:  BuildingChip,
                      }}
                      onChange={(selected) => {
                        field.onChange(selected.map((option) => option.value));
                        handleChangeBuilding(selected);
                      }}
                    />
                  )}
                />
                {errors.deal_proj_ids && (
                  <span className="tw-text-xs tw-text-red-500">
                    {errors.deal_proj_ids.message}
                  </span>
                )}
              </div>
            </div>

            {/* Reason */}
            <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-start tw-gap-2 tw-mb-2">
              <label className="tw-text-sm sm:tw-w-28 sm:tw-text-right sm:tw-pt-2 tw-shrink-0">
                Reason
              </label>
              <div className="tw-flex-1 tw-min-w-0">
                <Controller
                  control={control}
                  name="reason"
                  rules={{required: "Reason must be required."}}
                  render={({ field }) => (
                    <TinymceEditor
                      handleChange={field.onChange}
                      value={field.value}
                    />
                  )}
                />
                {errors.reason && (
                  <span className="tw-text-xs tw-text-red-500">
                    {errors.reason.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-5 tw-py-4">
            <Button type="submit" variant="primary" size="md">
              {building ? "Update" : "Add"} building
            </Button>
            <Button variant="secondary" size="md" onClick={closeModal}>
              Close
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
};

AddChangeBuildingForm.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  project: PropTypes.shape({
    id: PropTypes.number,
    building: PropTypes.object
  }),
  building: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    deal_proj_ids: PropTypes.array
  }),
  setWaiverBuildings: PropTypes.func.isRequired
}

export default AddChangeBuildingForm;